<template>
  <!-- Hidden element to calculate width -->
  <div
    v-if="visible"
    ref="measureRef"
    :class="[$style.tooltip, { [$style.interactive]: isInteractive, [$style.scrollableTooltip]: !!scrollable }]"
    :style="tooltipOuterStyle"
    aria-hidden="true"
    style="position: fixed; top: -9999px; left: -9999px; visibility: hidden"
  >
    <div :class="[$style.content, { [$style.scrollableContent]: !!scrollable }]" :style="contentStyle">
      <div :class="$style.text">
        <slot>{{ text }}</slot>
      </div>
      <a v-if="helpLink || linkLabel" :class="$style.helpLink">{{ linkLabel ?? $t("common.label.help") }}</a>
    </div>
  </div>

  <!-- Real tooltip -->
  <teleport v-if="visible" to="body">
    <div
      ref="tooltipRef"
      :class="[$style.tooltip, { [$style.interactive]: isInteractive, [$style.scrollableTooltip]: !!scrollable }]"
      :style="tooltipOuterStyle"
      popover="manual"
      @mouseenter="onTooltipEnter"
      @mouseleave="onTooltipLeave"
    >
      <div :class="[$style.content, { [$style.scrollableContent]: !!scrollable }]" :style="contentStyle">
        <div :class="$style.text">
          <slot>{{ text }}</slot>
        </div>
        <a
          v-if="helpLink || linkLabel"
          :class="$style.helpLink"
          :href="helpLink"
          :target="helpLink ? '_blank' : undefined"
          :rel="helpLink ? 'noopener noreferrer' : undefined"
          @click="handleLinkClick"
          >{{ linkLabel ?? $t("common.label.help") }}</a
        >
      </div>
      <div
        ref="arrowRef"
        :class="[$style.arrow, tooltipPosition === 'top' ? $style.arrowTop : $style.arrowBottom]"
        aria-hidden="true"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="5" viewBox="0 0 10 5" preserveAspectRatio="none">
          <path
            d="M0.515137 4.05811H9.51514L6.0758 0.618764C5.49001 0.0329781 4.54026 0.0329795 3.95448 0.618766L0.515137 4.05811Z"
            fill="currentColor"
          />
        </svg>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { TooltipStyles } from "./types";

interface Props {
  // Either id (looked up once on mount) or target (a reactive element ref) must be given.
  // target is for elements a framework (e.g. AG Grid) mounts/replaces asynchronously — since
  // it's a prop, attaching reacts to it becoming available or being swapped for a new node.
  id?: string;
  target?: HTMLElement | null;
  // Ignored when the default slot is used instead — falls back to it otherwise.
  text?: string;
  delay?: number;
  helpLink?: string;
  linkLabel?: string;
  styles?: TooltipStyles;
  scrollable?: boolean;
  alwaysVisible?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<(e: "link-click") => void>();
const { t: $t } = useI18n();
const slots = useSlots();

// Slotted content (e.g. a list of chips) needs the same hover-into-the-popover grace period as
// the help-link/scrollable cases, so the pointer can travel from the trigger into it.
const isInteractive = computed(() => !!props.helpLink || !!props.linkLabel || !!props.scrollable || !!slots.default);

// helpLink navigates externally; without it, linkLabel triggers link-click instead (e.g. opening
// an in-app dialog) so callers aren't forced into a URL-only tooltip action.
function handleLinkClick(event: MouseEvent) {
  if (props.helpLink) return;
  event.preventDefault();
  emit("link-click");
}

// Expand a CSS padding shorthand into four explicit sides.
function parsePadding(p: string) {
  const v = p.trim().split(/\s+/);
  if (v.length === 1) return { top: v[0], right: v[0], bottom: v[0], left: v[0] };
  if (v.length === 2) return { top: v[0], right: v[1], bottom: v[0], left: v[1] };
  if (v.length === 3) return { top: v[0], right: v[1], bottom: v[2], left: v[1] };
  return { top: v[0], right: v[1], bottom: v[2], left: v[3] };
}

const tooltipOuterStyle = computed(() => {
  const s = props.styles;
  const style: Record<string, string> = {};
  if (s?.fontSize) style.fontSize = s.fontSize;
  if (s?.textColor) style.color = s.textColor;
  if (s?.maxWidth) style.maxWidth = s.maxWidth;
  if (s?.padding) style.padding = s.padding;
  if (props.scrollable && s?.contentPadding) {
    const p = parsePadding(s.contentPadding);
    style.paddingTop = p.top;
    style.paddingBottom = p.bottom;
  }
  return style;
});

const contentStyle = computed(() => {
  if (!props.scrollable) return {};
  const s = props.styles;
  const style: Record<string, string> = {};
  if (s?.maxHeight) style.maxHeight = s.maxHeight;
  if (s?.contentPadding) {
    const p = parsePadding(s.contentPadding);
    style.padding = `0 ${p.right} 0 ${p.left}`;
  }
  return style;
});

const tooltipRef = ref<HTMLDivElement | null>(null);
const measureRef = ref<HTMLDivElement | null>(null);
const arrowRef = ref<HTMLDivElement | null>(null);
const tooltipPosition = ref<"top" | "bottom">("top");

const visible = ref(false);

let showTimeout: number | null = null;
let hideTimeout: number | null = null;

const getTargetEl = (): HTMLElement | null => props.target ?? (props.id ? document.getElementById(props.id) : null);

const positionTooltip = () => {
  const el = getTargetEl();
  const tooltipEl = tooltipRef.value;
  const measureEl = measureRef.value;
  const arrowEl = arrowRef.value;
  if (!el || !tooltipEl || !measureEl || !arrowEl) return;

  const rectEl = el.getBoundingClientRect();
  // The hidden measurer inherits from wherever the component sits, the real tooltip from <body>;
  // where those differ the text wraps differently and the measured width is too small to clamp
  // against. Once the popover is on screen it can measure itself.
  const shownRect = tooltipEl.offsetWidth > 0 ? tooltipEl.getBoundingClientRect() : null;
  const rectTooltip = shownRect ?? measureEl.getBoundingClientRect();

  const pagePadding = 8;
  const arrowMargin = 20;

  let left = rectEl.left + rectEl.width / 2 - rectTooltip.width / 2;
  let top = rectEl.bottom + 6;

  const maxRight = window.innerWidth - rectTooltip.width - pagePadding;

  // A tooltip wider than the viewport leaves maxRight below pagePadding; pinning it to the left
  // edge keeps its start on screen instead of pushing it off the other side.
  left = Math.max(pagePadding, Math.min(left, maxRight));

  const maxBottom = window.innerHeight - rectTooltip.height - pagePadding;

  if (top > maxBottom) {
    top = rectEl.top - rectTooltip.height - 8;
    tooltipPosition.value = "bottom";
  } else {
    tooltipPosition.value = "top";
  }

  // The popover UA stylesheet sets `inset: 0`, which leaves `right`/`bottom` fighting the offsets
  // set here and makes the rendered width depend on the position it is given.
  tooltipEl.style.right = "auto";
  tooltipEl.style.bottom = "auto";
  tooltipEl.style.left = `${Math.round(left)}px`;
  tooltipEl.style.top = `${Math.round(top)}px`;

  const targetCenter = rectEl.left + rectEl.width / 2;
  const tooltipCenter = left + rectTooltip.width / 2;
  const diff = targetCenter - tooltipCenter;

  let arrowOffset = rectTooltip.width / 2 + diff;
  arrowOffset = Math.max(arrowMargin, Math.min(rectTooltip.width - arrowMargin, arrowOffset));

  arrowEl.style.left = `${Math.round(arrowOffset)}px`;
  arrowEl.style.transform = "translateX(-50%)";
};

const showNow = async () => {
  showTimeout = null;
  visible.value = true;
  await nextTick();
  positionTooltip();
  tooltipRef.value?.showPopover?.();
  // Now that it has layout, re-clamp against its real width.
  positionTooltip();
};

const show = () => {
  if (showTimeout) clearTimeout(showTimeout);

  showTimeout = window.setTimeout(() => {
    void showNow();
  }, props.delay ?? 600);
};

const hideNow = () => {
  if (props.alwaysVisible) return;
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
  tooltipRef.value?.hidePopover?.();
  visible.value = false;
};

const hide = () => {
  if (showTimeout) {
    clearTimeout(showTimeout);
    showTimeout = null;
  }

  // Interactive tooltips (help link or scrollable): give the pointer a brief moment to
  // travel from the target into the tooltip so it stays usable.
  if (isInteractive.value) {
    if (hideTimeout) clearTimeout(hideTimeout);
    hideTimeout = window.setTimeout(hideNow, 120);
    return;
  }

  hideNow();
};

const onTooltipEnter = () => {
  if (!isInteractive.value) return;
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
};

const onTooltipLeave = () => {
  if (!isInteractive.value) return;
  hideNow();
};

const bindListeners = (el: HTMLElement) => {
  el.addEventListener("mouseenter", show);
  el.addEventListener("mouseleave", hide);
  el.addEventListener("mousedown", hide);
  // A long press on a touch screen shows the tooltip and its release sends a click without a mousedown.
  el.addEventListener("click", hide);
};

const unbindListeners = (el: HTMLElement) => {
  el.removeEventListener("mouseenter", show);
  el.removeEventListener("mouseleave", hide);
  el.removeEventListener("mousedown", hide);
  el.removeEventListener("click", hide);
};

let targetObserver: MutationObserver | null = null;
let idAttachedEl: HTMLElement | null = null;

const tryAttachById = () => {
  if (!props.id) return false;
  const el = document.getElementById(props.id);
  if (!el) return false;
  bindListeners(el);
  idAttachedEl = el;
  return true;
};

const onScrollOrResize = () => {
  if (!visible.value) return;
  positionTooltip();
};

onMounted(() => {
  // id mode: the element may not exist yet (e.g. it mounts asynchronously) — watch the DOM and
  // attach once it shows up instead of failing silently. One-shot: `id` never changes post-mount.
  if (props.id && !tryAttachById()) {
    targetObserver = new MutationObserver(() => {
      if (tryAttachById()) {
        targetObserver?.disconnect();
        targetObserver = null;
      }
    });
    targetObserver.observe(document.body, { childList: true, subtree: true });
  }
  window.addEventListener("scroll", onScrollOrResize, true);
  window.addEventListener("resize", onScrollOrResize);
  if (props.alwaysVisible) void showNow();
});

// target mode: a reactive element ref (e.g. a sibling mounted/replaced by AG Grid) — rebind
// whenever it changes instead of only looking it up once.
watch(
  () => props.target,
  (newEl, oldEl) => {
    if (oldEl) unbindListeners(oldEl);
    if (newEl) bindListeners(newEl);
    // target can resolve (or be replaced) after showNow() already positioned against a stale
    // (null or removed) element — recompute once it's valid while the tooltip is on screen.
    if (newEl && visible.value) void nextTick().then(positionTooltip);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (idAttachedEl) unbindListeners(idAttachedEl);
  if (props.target) unbindListeners(props.target);
  targetObserver?.disconnect();
  targetObserver = null;
  if (showTimeout) clearTimeout(showTimeout);
  if (hideTimeout) clearTimeout(hideTimeout);
  window.removeEventListener("scroll", onScrollOrResize, true);
  window.removeEventListener("resize", onScrollOrResize);
});
</script>

<style lang="scss" module>
.tooltip {
  position: fixed;
  max-width: min(304px, calc(100vw - 16px));
  font-size: 10px;
  font-weight: 450;
  letter-spacing: 2%;
  line-height: 14px;
  text-align: center;
  white-space: normal;
  word-wrap: break-word;
  margin: 0;
  z-index: 9999;
  overflow: visible;
  color: var(--color-action-neutral-normal);
  background-color: var(--color-action-inverted-normal);
  border-radius: var(--border-radius-medium);
  border: 0px solid white;
  padding: var(--space-component-padding-small) var(--space-component-padding-medium);
  box-shadow:
    0px 2px 4px rgba(0, 0, 0, 0.25),
    0px 4px 8px rgba(0, 0, 0, 0.15);
}

.scrollableTooltip {
  padding: 0;
}

.scrollableContent {
  padding: 8px 12px;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.interactive .content {
  flex-direction: column;
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  vertical-align: top;
}

.text {
  text-align: left;
  white-space: pre-line;
}

.helpLink {
  color: var(--color-content-informative-primary);
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;
}

.arrow {
  position: absolute;
  top: -4px;
  width: 10px;
  height: 5px;
  pointer-events: none;
  color: var(--color-action-inverted-normal);

  svg {
    display: block;
    width: 10px;
    height: 5px;
    transform-origin: center center;
  }

  &.arrowTop {
    top: -4px;
    svg {
      transform: rotate(0deg);
      filter: drop-shadow(0px -2px 1px rgba(0, 0, 0, 0.05));
    }
  }

  &.arrowBottom {
    bottom: -4px;
    top: auto;
    svg {
      transform: rotate(180deg);
    }
  }
}
</style>
